import { ExclamationCircleOutlined, QrcodeOutlined } from '@ant-design/icons'
import { PDFDownloadLink } from '@react-pdf/renderer'
import { Button, Button as Button2, Form as Form2, Input, Modal, Space, Spin } from 'antd'
import QRCode from 'qrcode'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useRenderUserBadgeMutation, useGetRenderUserBadgeQuery } from '../../../api/v2/badge'
import { useGetArticleUsersQuery } from '../../../api/article.api'
import { useGetCompanionsQuery, useRemoveCompanionMutation } from '../../../api/companion.api'
import { useLazyGetConferencesQuery } from '../../../api/conference.api'
import {
  useCreateInviteMutation,
  useGetEventUserQuery,
  useUpdateEventDayUserMutation,
  useUpdateEventUserMutation,
} from '../../../api/eventUser.api'
import { useCreateUserMutation } from '../../../api/user.api'
import { useGetEmailsV3Query, useSendEmailV3Mutation } from '../../../api/v2/emailsV3'
import { useLazyGetResumeQuery } from '../../../api/v2/invoices'
import FormBuilder from '../../../components/FormBuilder'
import HTML from '../../../components/HTML'
import RecapPDF from '../../../components/RecapPDF/RecapPDF'
import { useMyContext } from '../../../context/context'
import useActionLog from '../../../hooks/useActionLog'
import useConf from '../../../hooks/useConf'
import useHistoryCustom from '../../../hooks/useHistoryCustom'
import useSettingsV2 from '../../../hooks/useSettingsV2'
import useTranslation from '../../../hooks/useTranslation'
import useTranslations from '../../../hooks/useTranslations'
import formatUserExtended from '../../../services/formatUserExtended'
import ROUTES from '../../../services/routes'
import { badgeFileName, downloadFile } from '../../../utils/badgeFile'
import { getDate, getWallDateTime2 } from '../../../utils/date'
import { getCompanionLabel } from '../../../utils/settings'
import Resume from '../resume'
import { useSteps } from '../Steps'
import { SettingsProvider } from '../../../hooks/useSettingsV3'
import AddAssociates from './addAssociates'
import Invoices from './Invoices'

const styles = {
  cardContainer: settings => ({
    padding: 10,
    border: `1px solid ${settings.bgPrimaryColor}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    flexWrap: 'wrap',
    gap: 12,
  }),
  name: {
    fontWeight: 700,
  },
  email: {
    fontSize: 12,
  },
}

function isSafari() {
  const { userAgent } = navigator
  return userAgent.indexOf('Safari') !== -1 && userAgent.indexOf('Chrome') === -1
}

function DownloadBadgeSafari({ userId, fileName }) {
  const { addLog } = useActionLog()
  const { myContext } = useMyContext()
  const { settings } = useSettingsV2()

  const { data, isLoading } = useGetRenderUserBadgeQuery({ ...myContext, userId })

  const openNewWindow = () => {
    downloadFile(data, fileName)
    addLog({ actionId: 6, userId, extended: { label: 'Badge' } })
  }

  return (
    <Button2 type="primary" loading={isLoading} onClick={openNewWindow}>
      {settings?.FO_MEETING_CONFIRMATION_BADGE_LABEL || 'Télécharger le badge'}
    </Button2>
  )
}

const Companion = ({ user, isCompanion, resume }) => {
  const [isLoading, setIsLoading] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [qrVisible, setQrVisible] = useState(false)

  const { myContext, event } = useMyContext()
  const { settings } = useSettingsV2()
  const { t } = useTranslations()
  const { resetSteps, participation, isClosed } = useSteps()

  const badgeName = badgeFileName({
    lastName: user.lastName,
    firstName: user.firstName,
    code: user.UserEvent?.code,
    eventName: event?.name,
  })
  const translation = useTranslation()
  const { addLog } = useActionLog()
  const [modal, contextHolder] = Modal.useModal()

  const [removeCompanion] = useRemoveCompanionMutation()
  const { data } = useGetArticleUsersQuery({ ...myContext, userId: user.id })
  const [downloadBadgeV2] = useRenderUserBadgeMutation()
  const [sendEmailV3, { isLoading: isSendingV3, isSuccess: isSendSuccessV3 }] = useSendEmailV3Mutation()
  const { data: emails } = useGetEmailsV3Query({ ...myContext, type: 'fo' })

  const backgroundColor = useMemo(() => {
    if (isCompanion) {
      return settings.FO_MEETING_CONFIRMATION_COMPANION_BACKGROUND_COLOR || 'white'
    }
    return settings.FO_MEETING_CONFIRMATION_BACKGROUND_COLOR || 'white'
  }, [isCompanion, settings])

  const color = useMemo(() => {
    if (isCompanion) {
      return settings.FO_MEETING_CONFIRMATION_COMPANION_TEXT_COLOR || 'black'
    }
    return settings.FO_MEETING_CONFIRMATION_TEXT_COLOR || 'black'
  }, [isCompanion, settings])

  const extended = formatUserExtended(translation, user, user?.UserEvent)

  const openNewWindow = async () => {
    setIsLoading(true)
    setIsSuccess(false)
    addLog({ actionId: 6, userId: user.id, extended: { label: 'Badge' } })

    await downloadBadgeV2({ ...myContext, userId: user.id })
      .unwrap()
      .then(url => downloadFile(url, badgeName))
    setIsLoading(false)
    setIsSuccess(true)
  }

  return (
    <>
      <Modal
        open={qrVisible}
        footer={null}
        onCancel={() => setQrVisible(false)}
        centered
        width="90vw"
        styles={{ body: { height: '85vh', display: 'flex', alignItems: 'center', justifyContent: 'center' } }}
      >
        <div style={{ textAlign: 'center', padding: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 16 }}>
            {user.firstName} {user.lastName}
          </div>
          {user.qrcode && (
            <img
              src={user.qrcode}
              alt="QR Code"
              style={{ width: 'min(80vw, 75vh)', height: 'min(80vw, 75vh)', imageRendering: 'pixelated' }}
            />
          )}
        </div>
      </Modal>
      <div style={{ ...styles.cardContainer(settings), backgroundColor, color, marginBottom: 4 }}>
        <div>
          <Space>
            <div style={styles.name}>
              {user.firstName} {user.lastName}
            </div>
            {/* {isCompanion && <div>({getCompanionLabel(settings, true)})</div>} */}
          </Space>
          <div style={styles.email}>{user.Authentication?.email}</div>
          <div style={{ fontSize: 14 }}>
            {settings?.FO_MEETING_CONFIRMATION_RECAP_EXTENDED &&
              settings?.FO_MEETING_CONFIRMATION_RECAP_EXTENDED.split(',').map(
                m =>
                  extended[m] && (
                    <div>
                      {extended[m]?.label} : {extended[m]?.value}
                    </div>
                  )
              )}
            {user.conferences?.map(m => (
              <div>{`${m.title} - ${getWallDateTime2(m.slot.startAt, m.slot.endAt)}`}</div>
            ))}
            {data?.details?.map(m => (
              <>
                <div style={{ fontWeight: 700 }}>{m.day ? getDate(m.day.day) : t('DETAILS')}</div>
                {m.articles.map(a => (
                  <div>- {`${a.label} (${a.price.toFixed(2)} € H.T.)`}</div>
                ))}
              </>
            ))}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {settings?.FO_EVENT_BADGE_TEMPLATE_ID &&
            user.qrcode &&
            settings?.FO_MEETING_CONFIRMATION_SHOW_DOWNLOAD_BADGE &&
            (isCompanion ? true : user?.UserEvent?.UserEventDays?.find(f => f.registered) && !user.UserEvent?.isRemote) &&
            !resume?.canPay && (
              <Button2 icon={<QrcodeOutlined />} onClick={() => setQrVisible(true)}>
                QR Code
              </Button2>
            )}
          {isCompanion &&
            user.Authentication?.email &&
            !user.UserEvent?.isRemote &&
            !resume?.canPay &&
            settings?.FO_MEETING_CONFIRMATION_SHOW_SEND_BADGE && (
              <Button2
                type="primary"
                loading={isSendingV3}
                onClick={async () => {
                  if (emails?.find(f => f.actionId === 4)) {
                    sendEmailV3({ ...myContext, actionId: 4, userId: user.id })
                  }
                }}
                disabled={isSendSuccessV3}
              >
                {settings?.FO_MEETING_CONFIRMATION_SEND_BADGE_LABEL || 'Envoyer le badge'}
              </Button2>
            )}
          {settings?.FO_EVENT_BADGE_TEMPLATE_ID &&
            settings?.FO_MEETING_CONFIRMATION_SHOW_DOWNLOAD_BADGE &&
            (isCompanion ? true : user?.UserEvent?.UserEventDays?.find(f => f.registered) && !user.UserEvent?.isRemote) &&
            !resume?.canPay && (
              <>
                {isSafari() ? (
                  <DownloadBadgeSafari userId={user.id} fileName={badgeName} />
                ) : (
                  <Button2 type="primary" loading={isLoading} onClick={openNewWindow} disabled={isSuccess}>
                    {settings?.FO_MEETING_CONFIRMATION_BADGE_LABEL || 'Télécharger le badge'}
                  </Button2>
                )}
              </>
            )}
          {settings?.FO_MEETING_CONFIRMATION_SHOW_EDIT_BUTTON !== false
            && (!settings?.FO_MEETING_COMPANIONS_AUTO_IS_ACTIVE || !isCompanion) && !isClosed && !user.UserEvent?.representedBy && (
            <Button2 onClick={() => (isCompanion ? resetSteps(5, user.id) : resetSteps())}>{t('EDIT')}</Button2>
          )}
          {contextHolder}
          {isCompanion && !isClosed && (
            <Button2
              type="primary"
              style={{ backgroundColor: 'black', borderColor: 'black' }}
              onClick={() => {
                if (settings?.FO_MEETING_COMPANIONS_REMOVE_HTML) {
                  modal.confirm({
                    title: <HTML style={{ margin: 0 }} name="FO_MEETING_COMPANIONS_REMOVE_HTML" />,
                    icon: <ExclamationCircleOutlined />,
                    okText: 'Oui',
                    okType: 'danger',
                    cancelText: 'Non',
                    onOk() {
                      removeCompanion({ ...myContext, userId: user.id })
                    },
                  })
                } else {
                  removeCompanion({ ...myContext, userId: user.id })
                }
              }}
            >
              {t('REMOVE')}
            </Button2>
          )}
        </div>
      </div>
    </>
  )
}

const Confirmation = () => {
  const location = useLocation()
  const query = new URLSearchParams(location.search)

  const [companions, setCompanions] = useState([])
  const [me, setMe] = useState()
  const [companionLoading, setCompanionLoading] = useState(false)
  const [showCompanionsModal, setShowCompanionsModal] = useState(false)
  const [showInvitations, setShowInvitations] = useState(false)
  const [isSend, setIsSend] = useState(false)
  const [showAssociates, setShowAssociates] = useState(false)
  const [isRegistered1, setIsRegistered1] = useState(false)
  const [isRegistered2, setIsRegistered2] = useState(true)

  const history = useHistoryCustom()
  const history2 = useHistory()
  const { event, myContext } = useMyContext()
  const { settings } = useSettingsV2()

  const { t } = useTranslations()
  const { participation, userId, resetSteps, isClosed, screenWidth } = useSteps()
  const { showCompanions, companionsMax, formV2Id, articlesActive } = useConf(participation)
  const [companionForm] = Form2.useForm()
  const [invitationForm] = Form2.useForm()
  const translation = useTranslation()
  const [formRef] = Form2.useForm()
  const [formRef2] = Form2.useForm()
  const { settings: settingsV2 } = useSettingsV2()

  const { data: meData, isLoading: isLoading1 } = useGetEventUserQuery(myContext)
  const { data: otherData } = useGetEventUserQuery({ ...myContext, userId })
  const { data, isLoading: isLoading2 } = useGetCompanionsQuery(myContext)
  const [getConferences] = useLazyGetConferencesQuery()
  const [createUser] = useCreateUserMutation()
  const [updateEventUser] = useUpdateEventUserMutation()
  const [updateEventDay] = useUpdateEventDayUserMutation()
  const [createInvite, { isLoading: isInviting }] = useCreateInviteMutation()
  const [fetchResume, { data: resume }] = useLazyGetResumeQuery()
  const [sendEmailV3] = useSendEmailV3Mutation()
  const { data: emails } = useGetEmailsV3Query({ ...myContext, type: 'fo' })

  const isLoading = useMemo(() => isLoading1 || isLoading2, [isLoading1, isLoading2])

  useActionLog({ actionId: 2 })

  const isPresent = participation === 1 || participation === 4

  useEffect(() => {
    if (!articlesActive) return
    const uId = userId || myContext?.userId
    fetchResume({ ...myContext, userId: uId })
  }, [articlesActive, fetchResume, myContext, userId])

  // En vue companion (participation === 5) le state useSteps reste pollué après le
  // flow d'ajout d'un accompagnant : otherData pointe alors sur l'accompagnant et
  // les onAfterInit valident le formulaire de l'accompagnant, ce qui mettait
  // isRegistered2=false et affichait « Je m'inscris » à tort. Côté host, seul
  // isRegistered1 doit décider.
  const isRegistered = useMemo(() => (participation === 5 ? isRegistered1 : isRegistered1 && isRegistered2), [
    isRegistered1,
    isRegistered2,
    participation,
  ])

  useEffect(() => {
    setIsRegistered1(meData?.UserEvent?.registered || meData?.UserEvent?.representedBy)
  }, [meData])

  const sendEmailCb = async () => {
    if (!meData) {
      return null
    }

    if (participation === 1) {
      if (emails?.find(f => f.actionId === 2)) {
        return sendEmailV3({ ...myContext, actionId: 2, userId: meData.id }).unwrap()
      }
    }

    // TODO : Ajouter un email spécifique pour accompagnant
    if (participation === 5 && !articlesActive) {
      if (emails?.find(f => f.actionId === 19)) {
        return sendEmailV3({ ...myContext, actionId: 19, userId }).unwrap()
      }
    }

    if (isPresent) {
      if (emails?.find(f => f.actionId === 1)) {
        return sendEmailV3({ ...myContext, actionId: 1, userId: meData.id }).unwrap()
      }
    }

    const fromUser =
      meData.firstName || meData.lastName
        ? `${meData.firstName || ''} ${meData.lastName || ''} ${meData.Authentication?.email ? `(${meData.Authentication?.email})` : ''}`
        : meData.Authentication?.email || ''

    if (participation === 2) {
      if (emails?.find(f => f.actionId === 5)) {
        await sendEmailV3({ ...myContext, actionId: 5, userId, variables: { fromUser } }).unwrap()
      }

      if (emails?.find(f => f.actionId === 6)) {
        await sendEmailV3({ ...myContext, actionId: 6, userId: meData.id, variables: { fromUser } }).unwrap()
      }
    }

    if (participation === 3) {
      if (emails?.find(f => f.actionId === 7)) {
        return sendEmailV3({ ...myContext, actionId: 7, userId: meData.id }).unwrap()
      }
    }

    return null
  }

  useEffect(() => {
    if (meData && settings && !isSend && !resume?.canPay && emails) {
      setIsSend(true)
      sendEmailCb()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meData, settings, isSend, resume, emails])

  useEffect(() => {
    const ok = query.get('status') === 'success'
    if (ok && meData && settings) {
      if (emails?.find(f => f.actionId === 8)) {
        sendEmailV3({ ...myContext, actionId: 8, userId: meData.id }).unwrap()
      }

      history2.push({ search: '' })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meData])

  const getConfirmationMessage = useCallback(() => {
    if (!me || !settings) {
      return null
    }

    if (isClosed) {
      return <HTML name="FO_MEETING_CONFIRMATION_REGISTER_CLOSED" />
    }

    if (resume?.canPay && articlesActive) {
      return <HTML name="FO_MEETING_ARTICLES_TO_PAY_HTML" />
    }

    if (!isRegistered) {
      return <HTML name="FO_MEETING_CONFIRMATION_NOT_PRESENT_HTML" />
    }

    if (me?.UserEvent?.representedBy && settings.FO_MEETING_CONFIRMATION_REPRESENTED_HTML) {
      return <HTML name="FO_MEETING_CONFIRMATION_REPRESENTED_HTML" />
    }

    if (me?.UserEvent?.registered && me?.UserEvent?.isRemote && settings.FO_MEETING_CONFIRMATION_REMOTE_HTML) {
      return <HTML name="FO_MEETING_CONFIRMATION_REMOTE_HTML" />
    }

    if (me?.UserEvent?.registered && showCompanions && settings.FO_MEETING_CONFIRMATION_COMPANIONS_HTML) {
      return <HTML name="FO_MEETING_CONFIRMATION_COMPANIONS_HTML" />
    }

    if (me?.UserEvent?.registered && settings.FO_MEETING_CONFIRMATION_PRESENT_HTML) {
      return <HTML name="FO_MEETING_CONFIRMATION_PRESENT_HTML" />
    }

    return (
      <article className="message is-success field" style={{ textAlign: 'center' }}>
        <div className="message-body">
          <strong>Votre inscription a bien été prise en compte.</strong>
          <div>Vous recevrez une confirmation par mail dans quelques minutes.</div>
          <div>(Pensez à vérifier vos spams)</div>
        </div>
      </article>
    )
  }, [articlesActive, isClosed, isRegistered, me, resume, settings, showCompanions])

  useEffect(() => {
    if (data) {
      Promise.all(
        data.users.map(async m => {
          const c = await getConferences({ ...myContext, userId: m.id }).unwrap()
          // Le QR de chaque accompagnant porte SON code de participation, pas celui du porteur.
          const qrcode = m.UserEvent?.code ? await QRCode.toDataURL(m.UserEvent.code) : undefined
          return { ...m, conferences: c, qrcode }
        })
      ).then(setCompanions)
    }
  }, [data, getConferences, meData, myContext])

  useEffect(() => {
    if (meData) {
      getConferences({ ...myContext, userId: meData.id })
        .unwrap()
        .then(async c => {
          const qrcode = await QRCode.toDataURL(meData?.UserEvent?.code || '123')
          setMe({ ...meData, conferences: c, qrcode })
        })
    }
  }, [getConferences, meData, myContext])

  const createCompanion = async (values = {}) => {
    try {
      setCompanionLoading(true)

      const u = await createUser({
        ...myContext,
        user: {
          lastName: values?.lastName || settings?.FO_MEETING_COMPANIONS_AUTO_LASTNAME || 'Nom',
          firstName: values?.firstName,
          isExhibitor: settings?.FO_MEETING_IS_EXHIBITOR_DEFAULT,
        },
      }).unwrap()

      await updateEventUser({
        ...myContext,
        userId: u.id,
        eventUser: {
          registeredBy: `${meData.firstName || ''} ${meData.lastName || ''} (${meData.id})`,
          registeredBy2: meData.id,
          registeredFrom: localStorage.getItem('canal') || 'WEB',
          isRemote: false,
          extended: settings?.FO_MEETING_COMPANIONS_COPY_EXTENDED ? meData?.UserEvent?.extended || {} : {},
          typo: meData?.UserEvent?.typo,
          typoId: meData?.UserEvent?.typoId,
        },
      })
      const promises = [
        ...event.days.map(ed => {
          return updateEventDay({
            ...myContext,
            userId: u.id,
            dayId: ed.id,
            day: { registered: true, registeredAt: new Date() },
          }).unwrap()
        }),
      ]
      await Promise.all(promises)
    } finally {
      setCompanionLoading(false)
    }
  }

  const onAfterInit2 = useCallback(async () => {
    if (settingsV2?.FO_MEETING_INFORMATIONS_2_FORM_ID && otherData) {
      const isValid = await formRef2
        .validateFields()
        .then(() => true)
        .catch(() => false)
      setIsRegistered2(isValid)
    }
  }, [formRef2, otherData, settingsV2])

  const onAfterInit = useCallback(async () => {
    if (formV2Id && otherData) {
      const isValid = await formRef
        .validateFields()
        .then(() => true)
        .catch(() => false)
      setIsRegistered2(isValid)
    }
  }, [formRef, formV2Id, otherData])

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <Spin />
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
      <div
        style={{
          display: 'flex',
          flexDirection: screenWidth < 800 ? 'column' : 'row',
          justifyContent: 'space-between',
          width: '100%',
          marginBottom: 16,
          gap: 16,
        }}
      >
        <div style={{ flexGrow: 1 }}>{getConfirmationMessage()}</div>
        <Resume />
      </div>

      <div style={{ width: '100%', maxWidth: 800, gap: 32, display: 'flex', flexDirection: 'column' }}>
        {isRegistered && me && <Companion user={me} resume={resume} />}
        {!isRegistered && !isClosed && (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <Button type="primary" size="large" onClick={() => resetSteps()}>
              {t('I_REGISTER')}
            </Button>
          </div>
        )}

        {companions.length > 0 && (
          <div>
            <div style={{ width: '100%', color: 'black', marginBottom: 8, fontWeight: 500 }}>
              Vous avez inscrit{' '}
              <span style={{ color: settings.bgPrimaryColor }}>
                {companions.length} {getCompanionLabel(settings)}
              </span>
            </div>
            {companions.map(u => (
              <Companion key={`companion-${u.id}`} user={u} isCompanion resume={resume} />
            ))}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <div style={{ display: 'flex', gap: 8 }}>
            {isRegistered && !meData.UserEvent?.isRemote && !meData.UserEvent?.representedBy ? (
              <>
                {showCompanions && (
                  <>
                    <AddAssociates show={showAssociates} setShow={setShowAssociates} />
                    <Modal
                      title={t('REGISTER_COMPANION')}
                      visible={showCompanionsModal}
                      onCancel={() => setShowCompanionsModal(false)}
                      footer={
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Button onClick={() => setShowCompanionsModal(false)}>{t('CANCEL')}</Button>
                          <Button type="primary" onClick={() => companionForm.submit()} loading={companionLoading}>
                            Ajouter
                          </Button>
                        </div>
                      }
                    >
                      <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <div style={{ width: '100%', maxWidth: 600 }}>
                          <Form2
                            form={companionForm}
                            onFinish={async values => {
                              await createCompanion(values)
                              setShowCompanionsModal(false)
                              companionForm.resetFields()
                            }}
                          >
                            <Form2.Item label="Prénom" name="firstName" rules={[{ required: true, message: 'Ce champ est obligatoire' }]}>
                              <Input />
                            </Form2.Item>
                            <Form2.Item label="Nom" name="lastName" rules={[{ required: true, message: 'Ce champ est obligatoire' }]}>
                              <Input />
                            </Form2.Item>
                          </Form2>
                        </div>
                      </div>
                    </Modal>
                    {!isClosed && (
                      <Button2
                        type="primary"
                        onClick={async () => {
                          if (settings?.FO_MEETING_COMPANIONS_ASSOCIATES_IS_ACTIVE) {
                            setShowAssociates(true)
                          } else if (settings?.FO_MEETING_COMPANIONS_AUTO_IS_ACTIVE) {
                            await createCompanion()
                          } else if (settings?.FO_MEETING_COMPANIONS_AUTO_NAMES_IS_ACTIVE) {
                            setShowCompanionsModal(true)
                          } else {
                            resetSteps(5, null)
                          }
                        }}
                        disabled={companions.length >= companionsMax}
                      >
                        {t('REGISTER_COMPANION')}
                      </Button2>
                    )}
                  </>
                )}

                {settings?.FO_MEETING_ACTIVE_INVITE_USER && (
                  <>
                    <Button2
                      type="primary"
                      onClick={async () => {
                        setShowInvitations(true)
                      }}
                    >
                      {settings?.FO_MEETING_INVITE_USER_TEXT || "Inviter une personne à l'événement"}
                    </Button2>
                    <Modal
                      title={settings?.FO_MEETING_INVITE_USER_TEXT || "Inviter une personne à l'événement"}
                      visible={showInvitations}
                      onCancel={() => setShowInvitations(false)}
                      footer={
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Button onClick={() => setShowInvitations(false)}>{t('CANCEL')}</Button>
                          <Button type="primary" onClick={() => invitationForm.submit()} loading={isInviting}>
                            Inviter
                          </Button>
                        </div>
                      }
                    >
                      <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <div style={{ width: '100%', maxWidth: 600 }}>
                          <Form2
                            form={invitationForm}
                            onFinish={async values => {
                              const { newUserId } = await createInvite({ ...myContext, user: values }).unwrap()
                              if (emails?.find(f => f.actionId === 10)) {
                                await sendEmailV3({ ...myContext, actionId: 10, userId: newUserId }).unwrap()
                              }

                              setShowInvitations(false)
                              invitationForm.resetFields()
                            }}
                          >
                            <Form2.Item
                              label={t('EMAIL')}
                              name="email"
                              rules={[
                                { required: true, message: 'Ce champ est obligatoire' },
                                { type: 'email', message: "Le format de l'email est incorrect" },
                              ]}
                            >
                              <Input />
                            </Form2.Item>
                            <Form2.Item
                              label={t('FIRST_NAME')}
                              name="firstName"
                              rules={[{ required: true, message: 'Ce champ est obligatoire' }]}
                            >
                              <Input />
                            </Form2.Item>
                            <Form2.Item
                              label={t('LAST_NAME')}
                              name="lastName"
                              rules={[{ required: true, message: 'Ce champ est obligatoire' }]}
                            >
                              <Input />
                            </Form2.Item>
                          </Form2>
                        </div>
                      </div>
                    </Modal>
                  </>
                )}
              </>
            ) : (
              <div />
            )}
          </div>
          <Space style={{ flexWrap: 'wrap' }}>
            {settings?.FO_PARTICIPATIONS_PDF_IS_ACTIVE && isRegistered && event && companions && me && !resume?.canPay && (
              <PDFDownloadLink
                style={{ width: '100%' }}
                document={<RecapPDF event={event} settings={settings} me={me} companions={companions} t={translation} />}
                fileName={`Récapitulatif_${me.firstName || ''}-${me.lastName || ''}.pdf`}
              >
                {({ loading }) => (
                  <Button2 style={{ width: '100%' }} type="primary" loading={loading}>
                    {settings?.FO_PARTICIPATIONS_PDF_LABEL || 'Télécharger le récapitulatif'}
                  </Button2>
                )}
              </PDFDownloadLink>
            )}
            {isPresent && settings?.startAt && new Date(settings.startAt) < new Date() && !settings.FO_MEETING_ACCESS_LIVE_BUTTON_HIDE && (
              <Button2
                type="primary"
                onClick={() => {
                  history.push(ROUTES.LIVE)
                }}
              >
                Accéder au live
              </Button2>
            )}

            {settings?.FO_MEETING_CONFIRMATION_BACK_HOME && (
              <Button2
                type="primary"
                onClick={() => {
                  history.push('')
                }}
              >
                {t('BACK_HOME')}
              </Button2>
            )}
          </Space>
        </div>
        <Invoices />
      </div>

      <div style={{ display: 'none' }}>
        {formV2Id && otherData && (
          <FormBuilder formRef={formRef} formIdDefault={formV2Id} userId={otherData.id} onAfterInit={onAfterInit} />
        )}
        {settingsV2?.FO_MEETING_INFORMATIONS_2_FORM_ID && otherData && (
          <FormBuilder
            formRef={formRef2}
            formIdDefault={settingsV2.FO_MEETING_INFORMATIONS_2_FORM_ID}
            userId={otherData.id}
            onAfterInit={onAfterInit2}
          />
        )}
      </div>
    </div>
  )
}

export default Confirmation
